import {useEffect,useRef,useState} from 'react';
import {CheckCircle2,Monitor,ShieldCheck} from 'lucide-react';
import {FirstPasswordSetup,FirstProfileSetup,InvitationSetup} from './AccountSetup';
import type {WebsiteLoginReceipt} from './AuthDialog';
import {ApiError,isAbortError} from './account/api';
import {displayName,maskPhone,useSession} from './account/session';
import {approveDesktopAuthorization,describeDesktopAuthorizationError,desktopRequestID,getDesktopAuthorization,type DesktopAuthorization} from './account/desktop';

export function DesktopAuthorizationPage({onLogin,loginReceipt}:{onLogin:()=>void;loginReceipt:WebsiteLoginReceipt|null}) {
 const session=useSession();
 const [requestID,setRequestID]=useState<string|null>(null);
 const [request,setRequest]=useState<DesktopAuthorization|null>(null);
 const [error,setError]=useState('');
 const [actionError,setActionError]=useState('');
 const [deadlineReached,setDeadlineReached]=useState(false);
 const [busy,setBusy]=useState(false);
 const [reload,setReload]=useState(0);
 const [profileDone,setProfileDone]=useState<WebsiteLoginReceipt|null>(null);
 const operation=useRef<AbortController|null>(null);
 const submitting=useRef(false);
 const opened=useRef<string|null>(null);
 useEffect(()=>{
  const id=desktopRequestID(window.location.search);setRequestID(id);
  if(!id)setError('请从客户端点击登录，重新打开授权页。');
 },[]);
 useEffect(()=>{
  if(!requestID)return;
  const controller=new AbortController();let timer:ReturnType<typeof setTimeout>;
  const read=async()=>{
   try{
    const next=await getDesktopAuthorization(requestID,controller.signal);
    if(controller.signal.aborted)return;
    setRequest(next);setError('');
    if(['completed','cancelled','expired'].includes(next.status))return;
   }catch(error){if(controller.signal.aborted)return;setError(describeDesktopAuthorizationError(error))}
   timer=setTimeout(()=>void read(),2000);
  };
  void read();return()=>{controller.abort();clearTimeout(timer)};
 },[requestID,reload]);
 useEffect(()=>()=>operation.current?.abort(),[]);
 useEffect(()=>{
  if(!request)return;
  const remaining=Date.parse(request.expires_at)-Date.now();
  setDeadlineReached(remaining<=0);
  if(remaining<=0)return;
  const timer=setTimeout(()=>{setDeadlineReached(true);setReload(value=>value+1)},Math.min(remaining,600_000));
  return()=>clearTimeout(timer);
 },[request?.expires_at]);
 useEffect(()=>{
  if(request?.status!=='approved'||Date.parse(request.expires_at)<=Date.now()||opened.current===request.request_id)return;
  opened.current=request.request_id;
  // Browsers may decline a protocol launch. The visible link and client polling remain usable.
  window.location.assign(request.return_uri);
 },[request]);
 const approve=async()=>{
  if(!request||request.status!=='pending'||submitting.current)return;
  if(Date.parse(request.expires_at)<=Date.now()){setDeadlineReached(true);return}
  submitting.current=true;setBusy(true);setActionError('');
  const controller=new AbortController();operation.current=controller;
  try{
   await approveDesktopAuthorization(request.request_id,session.version,controller.signal);
   if(!controller.signal.aborted)setReload(value=>value+1);
  }catch(error){
   if(!controller.signal.aborted&&!isAbortError(error)){
    setActionError(describeDesktopAuthorizationError(error));
    if(error instanceof ApiError&&error.code===4126&&session.user)session.updateUser({id:session.user.id,has_password:false});
    if(error instanceof ApiError&&[404,409,410].includes(error.status))setReload(value=>value+1);
   }
  }
  finally{if(!controller.signal.aborted)setBusy(false);submitting.current=false}
 };
 const changeAccount=async()=>{operation.current?.abort();setBusy(false);setActionError('');await session.logout();onLogin()};
 const status=deadlineReached&&(request?.status==='pending'||request?.status==='approved')?'expired':request?.status;
 const terminal=status==='completed'||status==='cancelled'||status==='expired';
 return <section className="desktop-authorization" aria-labelledby="desktop-authorization-title">
  <div className="desktop-auth-card">
   <div className="desktop-auth-icon" aria-hidden="true">{status==='completed'?<CheckCircle2 size={30}/>:<Monitor size={30}/>}</div>
   <h1 id="desktop-authorization-title">{request?`登录 ${request.product_name} 客户端`:'客户端登录授权'}</h1>
   {request?.environment!=='prod'&&request&&<p className="desktop-auth-environment">{request.environment==='dev'?'开发环境':'测试环境'}</p>}
   {!request&&!error&&<p role="status">正在读取登录请求…</p>}
   {status==='pending'&&<>
    <p>请确认账号，再授权此客户端登录。请求有效期为 10 分钟。</p>
    {session.status==='checking'?<p role="status">正在恢复网页登录…</p>:session.status==='unavailable'?<><p role="alert">暂时无法恢复网页登录。</p><button className="auth-outline" onClick={session.retrySession}>重试网页登录</button></>:session.user?<>
     <div className="desktop-auth-account"><strong>{displayName(session.user)}</strong>{session.user.phone&&<span>{maskPhone(session.user.phone)}</span>}</div>
     {!session.user.has_password?<FirstPasswordSetup key={session.user.id}/>:loginReceipt?.isNew&&loginReceipt.user.id===session.user.id&&profileDone!==loginReceipt?<FirstProfileSetup key={session.user.id} onDone={()=>setProfileDone(loginReceipt)} wechatProfile={loginReceipt.method==='wechat'?{nickname:loginReceipt.user.nick_name,avatarUrl:loginReceipt.user.avatar_url}:undefined}/>:<>
      <InvitationSetup key={session.user.id} onAuthorize={approve} authorizing={busy}/>
     </>}
     <button className="auth-outline" disabled={busy} onClick={()=>void changeAccount()}>换个账号</button>
    </>:<button className="auth-primary" onClick={onLogin}>登录 / 注册</button>}
   </>}
   {(status==='approved'||status==='consumed')&&<>
    <p className="desktop-auth-status" role="status">{status==='approved'?'已批准，等待客户端接收':'客户端正在保存登录状态'}</p>
    <p>浏览器没有打开客户端时，可以点击下方按钮，也可以手动打开客户端继续。</p>
   </>}
   {status==='completed'&&<><p className="desktop-auth-status" role="status">客户端已完成登录</p><p>点击下方按钮回到客户端，继续研究。</p></>}
   {(status==='approved'||status==='consumed'||status==='completed')&&<a className="auth-primary" href={request!.return_uri}>打开 {request!.product_name} 客户端</a>}
   {status==='cancelled'&&<><p className="desktop-auth-status" role="status">登录请求已取消</p><p>如需登录，请回到客户端重新发起。</p></>}
   {status==='expired'&&<><p className="desktop-auth-status" role="status">登录请求已过期</p><p>请回到客户端重新点击登录。</p></>}
   {error&&<div role="alert"><p className="auth-error">{error}</p>{requestID&&!terminal&&<button className="auth-outline" onClick={()=>setReload(value=>value+1)}>重试读取状态</button>}</div>}
   {actionError&&!terminal&&<p className="auth-error" role="alert">{actionError}</p>}
   <p className="desktop-auth-note"><ShieldCheck size={16} aria-hidden="true"/>网页和客户端分别保持登录，退出其中一端不影响另一端。</p>
  </div>
 </section>;
}
